/**
 * API: Cart
 * ------------------------------------------------------------------------------
 * - API methods to to interact with the Shopify Cart.
 * - These should eventually replace API methods in helpers/cart.js
 *
 * @namespace Cart
 */
import { removeItem } from '@shopify/theme-cart'

/**
 * Default config for a given API call.
 * - Allows for Shopify endpoints to correctly return error states.
 */
const defaultConfig = {
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json',
  },
  method: 'POST',
}

/**
 * Query the Shopify cart and return a response.
 * @return {Object}
 */
export function fetchCart() {
  const queue = [
    fetch(`${mint.routes.cart.endpoint}?no-cache=${new Date().getTime()}`),
    fetch(`${mint.routes.cart.url}?view=resources&no-cache=${new Date().getTime()}`),
  ]

  return new Promise((resolve, reject) => {
    return Promise.all(queue).then((responses) => {
      return Promise.all(responses.map((response) => {
        return response.json()
      }))
    }).then((data) => {
      const cart = data[0]
      const extra = data[1]

      /**
       * Merge the data.
       */
      const items = cart.items.map((item) => {
        const match = extra.find((extraItem) => {
          return extraItem.key === item.key
        })

        if (!match) {
          return item
        }

        return {
          ...item,
          ...match,
        }
      })

      /**
       * Merge cart items and set state.
       */
      const newCart = {
        ...cart,
        items,
      }

      resolve(newCart)

    }).catch((error) => {
      reject(error)
    })
  })
}

/**
   * Add items to the cart.
   * - Accepts a single item entry or an array of items.
   * @example:
   * mint.EventBus.dispatch('cart:addItems', {
   *  data: {
   *    id: 123,
   *    quantity: 1
   *  },
   *  preventDrawer: true
   * })
   *
   * mint.EventBus.dispatch('cart:addItems', {
   *  data: [
   *   {
   *    id: 123,
   *    quantity: 1
   *  },
   *  {
   *   id: 456,
   *   quantity: 2
   *  }
   * })
   *
   * @param {Array|Object} items - Cart item(s) to add to the Cart.
   * @param {Array} items.item.id - Variant ID of the product to add.
   * @param {Array} items.item.quantity - Numerical quantity of an item to add.
   * @return {Object}
   */
export function addItems(items = []) {
  return new Promise((resolve, reject) => {
    fetch(mint.routes.cart.add, {
      ...defaultConfig,
      body: JSON.stringify(items),
    })
      .then((response) => response.json())
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

/**
 * Change Line Item
 * - Uses Shopify 'Change Cart' AJAX API endpoint
 * - Pass in Line item key instead of ID
 * - https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
 * @return {Object}
 */
export function changeItem(item) {
  return new Promise((resolve, reject) => {
    fetch(mint.routes.cart.change, {
      ...defaultConfig,
      body: JSON.stringify(item),
    })
      .then((response) => response.json())
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

/**
 * Updates cart attributes and note.
 * @param {Object} payload - Action payload.
 * @param {Object} [payload.attributes] - Attributes to set.
 * @param {String} [payload.note] - Cart note to set.
 * @returns {Promise}
 */
export function updateAttributes(payload) {
  return new Promise((resolve, reject) => {
    fetch(mint.routes.cart.update, {
      ...defaultConfig,
      body: JSON.stringify(payload),
    })
      .then((response) => response.json())
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        reject(error)
      })
  })
}

/**
 * Remove an item from the cart.
 * @param {String} key - Cart line item key.
 * @return {Object}
 */
export function remove(key) {
  return new Promise((resolve, reject) => {
    removeItem(key)
      .then((response) => resolve(response))
      .catch((error) => reject(error))
  })
}

/**
 * Clear the cart entirely.
 * @return {Object}
 */
export function clear() {
  return new Promise((resolve, reject) => {
    fetch(mint.routes.cart.clear, {
      ...defaultConfig,
    })
      .then((response) => response.json())
      .then((response) => {
        resolve(response)
      })
      .catch((error) => {
        reject(error)
      })
  })
}